/**
 * The old web's "Save Skill" card (item BR) — `Message.tsx`'s "main deliver
 * artifact button", at the bottom of an assistant reply.
 *
 * When a turn delivered code, the reply ends with a small card: an uppercase
 * SAVE SKILL header strip, then one row with the sparkle glyph, the artifact's
 * display name and a chevron. Pressing it opens the same source modal a step
 * row opens — the code that ran, and the Save Skill button. The card is the
 * visible cue that this turn produced something worth keeping; the step row's
 * "Show Source Code" is provenance, and nobody expected to find saving there.
 *
 * It appears only once the reply is complete and nothing is waiting on the
 * user (the old web's `isContentComplete && !hasPendingInterrupt`), and only
 * for the last **deliver** artifact — see `deliverArtifact.ts`.
 */
import { useState } from "react";
import { useLocale } from "../../i18n/LocaleProvider";
import type { AgentStep } from "../../lib/chat";
import { artifactTitle, mainDeliverStepIndex } from "../../lib/deliverArtifact";
import { ChevronRightIcon } from "../skills/icons";
import { ArtifactDialog, type SaveSkillHandler } from "./ArtifactDialog";

/** lucide `Sparkles` — the old web's glyph for a generated deliverable. */
function SparklesIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
      <path d="M20 3v4" />
      <path d="M22 5h-4" />
      <path d="M4 17v2" />
      <path d="M5 18H3" />
    </svg>
  );
}

export function SaveSkillCard({ steps, onSaveSkill }: { steps: AgentStep[] | undefined; onSaveSkill: SaveSkillHandler | null }) {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  const index = mainDeliverStepIndex(steps);
  const step = index >= 0 ? steps?.[index] : undefined;
  const artifact = step?.artifact;
  if (!artifact) return null;
  const title = artifactTitle(artifact, t("message.showSourceCode"));

  return (
    <div className="save-skill-card">
      <div className="save-skill-card-head">{t("message.saveSkill")}</div>
      <button type="button" className="save-skill-card-row" onClick={() => setOpen(true)}>
        <span className="save-skill-card-icon" aria-hidden="true">
          <SparklesIcon />
        </span>
        <span className="save-skill-card-title">{title}</span>
        <span className="save-skill-card-chevron" aria-hidden="true">
          <ChevronRightIcon size={16} />
        </span>
      </button>
      {open ? <ArtifactDialog artifact={artifact} onSaveSkill={onSaveSkill} onClose={() => setOpen(false)} /> : null}
    </div>
  );
}
